import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { usePortalSettings } from '../auth/PortalSettingsContext';
import { api, Department, FormFieldSetting } from '../services/api';

// ── Step indicator ────────────────────────────────────────────
function StepBar({ current }: { current: number }) {
  const steps = ['Project Info', 'Details & Dates', 'Description', 'Requirements', 'Review & Submit'];
  return (
    <div style={{ display: 'flex', background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>
      {steps.map((label, i) => {
        const done   = i < current;
        const active = i === current;
        return (
          <div key={i} style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 8, padding: '14px 8px', fontSize: 13,
            borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
            color: active ? 'var(--accent)' : done ? 'var(--success-text)' : 'var(--text2)',
            fontWeight: active ? 500 : 400,
          }}>
            <div style={{
              width: 22, height: 22, borderRadius: '50%', display: 'flex',
              alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600,
              border: `1.5px solid ${active ? 'var(--accent)' : done ? 'var(--success)' : 'var(--border2)'}`,
              background: done ? 'var(--success-bg)' : active ? 'var(--info-bg)' : 'transparent',
              color: active ? 'var(--accent)' : done ? 'var(--success-text)' : 'var(--text3)',
              flexShrink: 0,
            }}>
              {done ? '✓' : i + 1}
            </div>
            <span style={{ whiteSpace: 'nowrap' }}>{label}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Help ──────────────────────────────────────────────────────
const FIELD_HELP: Record<string, { title: string; body: string }> = {
  businessOwner: {
    title: 'What is a Business Owner?',
    body: 'The Business Owner is the staff member responsible for the business outcomes of this project — typically the department head or manager who requested it.\n\nThis is NOT an IT role. It should be someone in your department.',
  },
  projectManager: {
    title: 'What is a Project Manager?',
    body: 'The Project Manager oversees day-to-day execution of the project. For most projects this will be assigned by IT after approval.',
  },
  priority: {
    title: 'How do I choose a priority?',
    body: '• High — Critical to operations, compliance, or safety.\n• Normal — Important but not urgent.\n• Low — Beneficial but not time-sensitive.',
  },
  budgeted: {
    title: 'What does Budgeted mean?',
    body: '• Yes — Funding has been identified.\n• No — Funding not yet identified.\n• TBD — Still being determined.',
  },
  objectives: {
    title: 'What are Objectives?',
    body: 'Specific, measurable goals this project will achieve.\n\nGood: "Reduce permit processing time by 50% by automating data entry from Tyler EPL."\nNot helpful: "Improve the permitting process."',
  },
  deliverables: {
    title: 'What are Deliverables?',
    body: 'Tangible outputs produced at the end of the project.\n\nExamples: a new software system, a data migration, a configured integration, a training program.',
  },
  organizationalBenefits: {
    title: 'What are Organizational Benefits?',
    body: 'How this project benefits The Woodlands Township and its residents.\n\nExamples: cost savings, time savings, improved resident service, reduced risk, compliance.',
  },
};

function HelpModal({ topic, onClose }: { topic: string; onClose: () => void }) {
  const content = FIELD_HELP[topic];
  if (!content) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 28, maxWidth: 460, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{content.title}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: 'var(--text2)' }}>✕</button>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text1)', lineHeight: 1.7, whiteSpace: 'pre-line' }}>{content.body}</div>
        <button className="btn btn-primary" style={{ marginTop: 18, width: '100%' }} onClick={onClose}>Got it</button>
      </div>
    </div>
  );
}

function HelpButton({ topic, onOpen }: { topic: string; onOpen: (t: string) => void }) {
  return (
    <button onClick={() => onOpen(topic)} style={{
      width: 16, height: 16, borderRadius: '50%', fontSize: 10, fontWeight: 700,
      background: 'var(--border2)', border: 'none', cursor: 'pointer',
      color: 'var(--text2)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      marginLeft: 5, flexShrink: 0, verticalAlign: 'middle',
    }} title="Help">?</button>
  );
}

function Field({ label, required, hint, helpTopic, onOpenHelp, children }: {
  label: string; required?: boolean; hint?: string;
  helpTopic?: string; onOpenHelp?: (t: string) => void; children: React.ReactNode;
}) {
  return (
    <div className="field-group">
      <label className="field-label" style={{ display: 'flex', alignItems: 'center' }}>
        {label}{required && <span className="req">*</span>}
        {helpTopic && onOpenHelp && <HelpButton topic={helpTopic} onOpen={onOpenHelp} />}
      </label>
      {children}
      {hint && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

// ── Requirement draft ─────────────────────────────────────────
interface RequirementDraft {
  id: number; category: string; question: string; isRequired: boolean;
}

const CATEGORIES = ['Functional', 'Technical', 'Security', 'Integration', 'Accessibility', 'Reporting', 'Training', 'Other'];

// ── Form state ────────────────────────────────────────────────
interface FormState {
  title:                  string;
  departmentID:           number | null;
  priority:               string;
  budgeted:               string;
  amountBudgeted:         string;
  startDate:              string;
  endDate:                string;
  businessOwnerName:      string;
  businessOwnerEmail:     string;
  projectManagerName:     string;
  projectManagerEmail:    string;
  projectDescription:     string;
  objectives:             string;
  deliverables:           string;
  organizationalBenefits: string;
}

const EMPTY_FORM: FormState = {
  title: '', departmentID: null, priority: '', budgeted: '',
  amountBudgeted: '', startDate: '', endDate: '',
  businessOwnerName: '', businessOwnerEmail: '', projectManagerName: '', projectManagerEmail: '',
  projectDescription: '', objectives: '', deliverables: '', organizationalBenefits: '',
};

// ── Validation ────────────────────────────────────────────────
function validateStep(step: number, form: FormState, fieldEnabled: (key: string) => boolean): string[] {
  const errors: string[] = [];
  if (step === 0) {
    if (!form.title.trim())             errors.push('Project name is required.');
    if (!form.departmentID)             errors.push('Department is required.');
    if (!form.businessOwnerName.trim()) errors.push('Business owner is required.');
    if (!form.businessOwnerEmail.trim()) {
      errors.push('Business owner email is required.');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.businessOwnerEmail.trim())) {
      errors.push('Business owner email must be a valid email address.');
    }
  }
  if (step === 1) {
    if (fieldEnabled('priority') && !form.priority) errors.push('Priority is required.');
    if (!form.budgeted)  errors.push('Budget status is required.');
    if (!form.startDate) errors.push('Start date is required.');
    if (!form.endDate)   errors.push('End date is required.');
    if (form.startDate && form.endDate && form.endDate <= form.startDate)
      errors.push('End date must be after start date.');
  }
  if (step === 2) {
    if (!form.projectDescription.trim())     errors.push('Project description is required.');
    if (!form.objectives.trim())             errors.push('Objectives are required.');
    if (!form.deliverables.trim())           errors.push('Deliverables are required.');
    if (!form.organizationalBenefits.trim()) errors.push('Organizational benefits are required.');

    // Catch pasting the same answer into multiple fields (e.g. Objectives === Deliverables).
    const normalize = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');
    const fields: [string, string][] = [
      ['Project Description',     form.projectDescription],
      ['Objectives',               form.objectives],
      ['Deliverables',             form.deliverables],
      ['Organizational Benefits',  form.organizationalBenefits],
    ];
    const duplicatePairs = new Set<string>();
    for (let i = 0; i < fields.length; i++) {
      for (let j = i + 1; j < fields.length; j++) {
        const [labelA, textA] = fields[i];
        const [labelB, textB] = fields[j];
        if (textA.trim() && textB.trim() && normalize(textA) === normalize(textB)) {
          duplicatePairs.add(`${labelA} and ${labelB}`);
        }
      }
    }
    if (duplicatePairs.size > 0) {
      errors.push(
        `${Array.from(duplicatePairs).join('; ')} contain identical text. Each should say something different — ` +
        `Objectives are the goals, Deliverables are what gets produced, Organizational Benefits is why it matters. Please rewrite so they're distinct.`
      );
    }
  }
  return errors;
}

// ── Page ──────────────────────────────────────────────────────
export function NewRequestPage() {
  const navigate       = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, dbUserID, dbDepartmentID, isAdmin, isDirector } = useAuth();
  const { maintenanceModeEnabled } = usePortalSettings();

  const draftId  = searchParams.get('draft')  ? parseInt(searchParams.get('draft')!)  : null;
  const reviseId = searchParams.get('revise') ? parseInt(searchParams.get('revise')!) : null;
  const isRevise = reviseId !== null;
  const submissionBlocked = maintenanceModeEnabled && !isAdmin && !isRevise;

  const [step, setStep]     = useState(0);
  const [form, setForm]     = useState<FormState>(EMPTY_FORM);
  const [departments, setDepts] = useState<Department[]>([]);
  const [fieldSettings, setFieldSettings] = useState<FormFieldSetting[]>([]);
  const [loadingDepts, setLoadingDepts]     = useState(true);
  const [loadingProject, setLoadingProject] = useState(!!(draftId || reviseId));
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [submitting, setSubmitting]   = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [draftSavedAt, setDraftSavedAt] = useState<Date | null>(null);
  const [helpTopic, setHelpTopic]     = useState<string | null>(null);

  // Draft project ID — set once we have a saved draft
  const [draftProjectId, setDraftProjectId] = useState<number | null>(draftId);

  // Attachments
  const [attachments, setAttachments] = useState<any[]>([]);
  const [uploadingFile, setUploadingFile] = useState(false);

  // Requirements
  const [requirements, setRequirements] = useState<RequirementDraft[]>([]);
  const [reqForm, setReqForm]   = useState({ category: '', question: '', isRequired: true });
  const [showReqForm, setShowReqForm] = useState(false);
  const [reqError, setReqError] = useState('');
  const [nextReqId, setNextReqId] = useState(1);

  // Approval chain (for Review step preview)
  const [chainSteps, setChainSteps] = useState<{ StepName: string; StepOrder: number }[]>([]);

  const set = (field: keyof FormState, value: any) => setForm(f => ({ ...f, [field]: value }));

  // Field setting helpers
  const fieldEnabled = useCallback((key: string): boolean => {
    const s = fieldSettings.find(f => f.FieldKey === key);
    return s ? s.IsEnabled : true; // default enabled if not in settings
  }, [fieldSettings]);

  const fieldRequired = useCallback((key: string): boolean => {
    const s = fieldSettings.find(f => f.FieldKey === key);
    return s ? s.IsEnabled && s.IsRequired : false;
  }, [fieldSettings]);

  // Load form field settings
  useEffect(() => {
    api.formSettings.get().then(setFieldSettings).catch(() => {});
  }, []);

  // Load enabled approval chain steps for the Review step preview
  useEffect(() => {
    api.admin.chain.list()
      .then(steps => setChainSteps(steps.filter(s => s.IsEnabled).sort((a, b) => a.StepOrder - b.StepOrder)))
      .catch(() => {}); // non-fatal — preview degrades gracefully
  }, []);

  // Load departments
  useEffect(() => {
    api.departments.list().then(setDepts).finally(() => setLoadingDepts(false));
  }, []);

  // Pre-populate project manager with logged-in user
  useEffect(() => {
    if (user?.name && !form.projectManagerName) {
      set('projectManagerName', user.name);
    }
    if (user?.email && !form.projectManagerEmail) {
      set('projectManagerEmail', user.email);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.name, user?.email]);

  // Load draft or project to revise
  useEffect(() => {
    const id = draftId || reviseId;
    if (!id) return;
    api.projects.get(id)
      .then(project => {
        setForm({
          title:                  project.Title || '',
          departmentID:           project.DepartmentID || null,
          priority:               project.Priority || '',
          budgeted:               project.Budgeted || '',
          amountBudgeted:         project.AmountBudgeted ? String(project.AmountBudgeted) : '',
          startDate:              project.StartDate ? project.StartDate.split('T')[0] : '',
          endDate:                project.EndDate   ? project.EndDate.split('T')[0]   : '',
          businessOwnerName:      project.BusinessOwnerName || '',
          businessOwnerEmail:     (project as any).BusinessOwnerEmail || '',
          projectManagerName:     project.ProjectManagerName  || user?.name  || '',
          projectManagerEmail:    project.ProjectManagerEmail || user?.email || '',
          projectDescription:     project.ProjectDescription || '',
          objectives:             project.Objectives || '',
          deliverables:           project.Deliverables || '',
          organizationalBenefits: project.OrganizationalBenefits || '',
        });
        if (draftId) {
          setDraftProjectId(draftId);
          setAttachments(project.attachments || []);
        }
      })
      .catch(e => console.error('Failed to load project', e))
      .finally(() => setLoadingProject(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId, reviseId]);

  const loadAttachments = useCallback(() => {
    if (!draftProjectId) return;
    api.attachments.list(draftProjectId).then(setAttachments).catch(() => {});
  }, [draftProjectId]);

  const selectedDept = departments.find(d => d.DepartmentID === form.departmentID);
  const ownDept = departments.find(d => d.DepartmentID === dbDepartmentID);
  // Non-blocking nudge — this is exactly the #24 failure mode: submitted under
  // IT instead of Recreation because nothing tied the dropdown to the submitter's
  // own department. Only fires once we know both departments; never blocks submit.
  const departmentMismatch = !!(dbDepartmentID && form.departmentID && form.departmentID !== dbDepartmentID && ownDept);

  function goNext() {
    const errors = validateStep(step, form, fieldEnabled);
    if (errors.length > 0) { setValidationErrors(errors); return; }
    setValidationErrors([]);
    setStep(s => s + 1);
  }

  function goBack() {
    setValidationErrors([]);
    setStep(s => s - 1);
  }

  function buildPayload(isDraft: boolean) {
    return {
      projectID:              draftProjectId,
      title:                  form.title,
      departmentID:           form.departmentID,
      priority:               fieldEnabled('priority') ? (form.priority || null) : null,
      budgeted:               form.budgeted || 'TBD',
      amountBudgeted:         form.budgeted === 'Yes' && form.amountBudgeted ? parseFloat(form.amountBudgeted) : null,
      startDate:              form.startDate || null,
      endDate:                form.endDate   || null,
      userObjectId:           user?.objectId || '',
      requestorEmail:         user?.email    || '',
      requestorName:          user?.name     || '',
      businessOwnerName:      form.businessOwnerName,
      businessOwnerEmail:     form.businessOwnerEmail,
      projectManagerName:     form.projectManagerName,
      projectManagerEmail:    form.projectManagerEmail,
      projectDescription:     form.projectDescription,
      objectives:             form.objectives,
      deliverables:           form.deliverables,
      organizationalBenefits: form.organizationalBenefits,
      isDraft,
    };
  }

  async function handleSaveDraft() {
    if (!form.title.trim()) {
      setSubmitError('Please enter a project name before saving a draft.');
      return;
    }
    setSavingDraft(true);
    setSubmitError(null);
    try {
      const result = await api.projects.saveDraft(buildPayload(true));
      setDraftProjectId(result.projectID);
      setDraftSavedAt(new Date());
      window.history.replaceState(null, '', `/new?draft=${result.projectID}`);
      loadAttachments();
    } catch (e: any) {
      setSubmitError(e.message || 'Failed to save draft.');
    } finally {
      setSavingDraft(false);
    }
  }

  async function handleDeleteAttachment(attachmentId: number) {
    if (!draftProjectId) return;
    try {
      await api.attachments.delete(draftProjectId, attachmentId);
      loadAttachments();
    } catch (e: any) {
      setSubmitError('Failed to remove attachment: ' + e.message);
    }
  }

  async function handleSubmit() {
    if (!user) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      let projectID: number;

      if (isRevise && reviseId) {
        // Project sent back for revision — resume the existing approval chain.
        await api.projects.resubmit(reviseId, buildPayload(false));
        projectID = reviseId;
      } else {
        // New submission, or promotion of a saved draft. createProject handles
        // both: if buildPayload carries a draft projectID it updates that row
        // in place and builds the approval chain; otherwise it inserts new.
        const result = await api.projects.create(buildPayload(false));
        projectID = result.projectID;
      }

      if (requirements.length > 0) {
        await Promise.all(requirements.map(req =>
          api.scope.create(projectID, {
            category:    req.category || null,
            question:    req.question,
            isRequired:  req.isRequired ? 1 : 0,
            addedByName: user.name || '',
          })
        ));
      }

      navigate(`/projects/${projectID}`);
    } catch (e: any) {
      setSubmitError(e.message || 'Failed to submit project. Please try again.');
      setSubmitting(false);
    }
  }

  function addRequirement() {
    if (!reqForm.question.trim()) { setReqError('Please enter a requirement or question.'); return; }
    setRequirements(r => [...r, { id: nextReqId, category: reqForm.category, question: reqForm.question.trim(), isRequired: reqForm.isRequired }]);
    setNextReqId(n => n + 1);
    setReqForm({ category: '', question: '', isRequired: true });
    setReqError('');
    setShowReqForm(false);
  }

  function removeRequirement(id: number) {
    setRequirements(r => r.filter(req => req.id !== id));
  }

  // ── Step 1 — Project Info ─────────────────────────────────────
  const step0 = (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      <Field label="Project Name" required>
        <input className="field-input" placeholder="Enter a clear, descriptive project name"
          value={form.title} onChange={e => set('title', e.target.value)} />
      </Field>
      <Field label="Requestor">
        <input className="field-input" value={user?.name || ''} readOnly style={{ background: 'var(--surface2)', color: 'var(--text2)' }} />
      </Field>
      <Field label="Requestor Email">
        <input className="field-input" value={user?.email || ''} readOnly style={{ background: 'var(--surface2)', color: 'var(--text2)' }} />
      </Field>
      <Field label="Department" required>
        <select className="field-select" value={form.departmentID ?? ''} onChange={e => set('departmentID', Number(e.target.value))} disabled={loadingDepts}>
          <option value="">{loadingDepts ? 'Loading departments...' : 'Select department...'}</option>
          {departments.map(d => <option key={d.DepartmentID} value={d.DepartmentID}>{d.DepartmentName}</option>)}
        </select>
      </Field>
      {departmentMismatch && (
        <div style={{ gridColumn: '1 / -1', padding: '10px 14px', background: 'var(--warning-bg, #fffbeb)', border: '1px solid var(--warning, #f59e0b)', borderRadius: 8, fontSize: 12, color: 'var(--warning-text, #92400e)' }}>
          ⚠ You selected <strong>{selectedDept?.DepartmentName}</strong>, but your own department on file is <strong>{ownDept?.DepartmentName}</strong>. If you're submitting this on behalf of {selectedDept?.DepartmentName}, please double-check the Business Owner name and email below are correct for that department — the approval chain will route to {selectedDept?.DepartmentName}'s Director and EMT contact, not yours.
        </div>
      )}
      <Field label="Director" hint="Auto-populated from department">
        <input className="field-input" value={selectedDept?.DirectorName || ''} readOnly
          style={{ background: 'var(--surface2)', color: selectedDept ? 'var(--text1)' : 'var(--text3)' }} placeholder="Select a department above" />
      </Field>
      <Field label="Director Email" hint="Auto-populated from department">
        <input className="field-input" value={selectedDept?.DirectorEmail || ''} readOnly
          style={{ background: 'var(--surface2)', color: selectedDept ? 'var(--text1)' : 'var(--text3)' }} placeholder="Select a department above" />
      </Field>
      <Field label="EMT Contact" hint="Auto-populated from department">
        <input className="field-input" value={selectedDept?.AGMName || ''} readOnly
          style={{ background: 'var(--surface2)', color: selectedDept ? 'var(--text1)' : 'var(--text3)' }} placeholder="Select a department above" />
      </Field>
      <Field label="EMT Email" hint="Auto-populated from department">
        <input className="field-input" value={selectedDept?.AGMEmail || ''} readOnly
          style={{ background: 'var(--surface2)', color: selectedDept ? 'var(--text1)' : 'var(--text3)' }} placeholder="Select a department above" />
      </Field>
      <Field label="Business Owner" required hint="Person in your department responsible for the project outcomes" helpTopic="businessOwner" onOpenHelp={setHelpTopic}>
        <input className="field-input" placeholder="Enter business owner name" value={form.businessOwnerName} onChange={e => set('businessOwnerName', e.target.value)} />
      </Field>
      <Field label="Business Owner Email" required hint="Used to reach the business owner directly if IT has questions about this request">
        <input className="field-input" type="email" placeholder="name@thewoodlands-tx.gov" value={form.businessOwnerEmail} onChange={e => set('businessOwnerEmail', e.target.value)} />
      </Field>
      <Field label="Project Manager" hint="IT project manager — leave as-is if unknown" helpTopic="projectManager" onOpenHelp={setHelpTopic}>
        <input className="field-input" placeholder="Enter project manager name" value={form.projectManagerName} onChange={e => set('projectManagerName', e.target.value)} />
      </Field>
      <Field label="Project Manager Email" hint="Used to route reply notifications on comments/questions — leave as-is if unknown">
        <input className="field-input" type="email" placeholder="name@thewoodlands-tx.gov" value={form.projectManagerEmail} onChange={e => set('projectManagerEmail', e.target.value)} />
      </Field>
    </div>
  );

  // ── Step 2 — Details & Dates ──────────────────────────────────
  const step1 = (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      {fieldEnabled('priority') && (
        <Field label="Priority" required={fieldRequired('priority')} helpTopic="priority" onOpenHelp={setHelpTopic}>
          <select className="field-select" value={form.priority} onChange={e => set('priority', e.target.value)}>
            <option value="">Select priority...</option>
            <option value="High">High — Critical to operations or compliance</option>
            <option value="Normal">Normal — Important but not urgent</option>
            <option value="Low">Low — Nice to have</option>
          </select>
        </Field>
      )}
      <Field label="Budgeted" required helpTopic="budgeted" onOpenHelp={setHelpTopic}>
        <select className="field-select" value={form.budgeted} onChange={e => set('budgeted', e.target.value)}>
          <option value="">Select...</option>
          <option value="Yes">Yes — Funding identified</option>
          <option value="No">No — Funding not yet identified</option>
          <option value="TBD">TBD — To be determined</option>
        </select>
      </Field>
      {form.budgeted === 'Yes' && (
        <Field label="Estimated Budget Amount" hint="Enter approximate cost in USD">
          <input className="field-input" type="number" placeholder="0.00" min="0"
            value={form.amountBudgeted} onChange={e => set('amountBudgeted', e.target.value)} />
        </Field>
      )}
      <Field label="Requested Start Date" required>
        <input className="field-input" type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)} />
      </Field>
      <Field label="Requested End Date" required>
        <input className="field-input" type="date" value={form.endDate} onChange={e => set('endDate', e.target.value)} />
      </Field>
    </div>
  );

  // ── Step 3 — Description ──────────────────────────────────────
  const step2 = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Field label="Description of Project" required hint="What is this project and why is it needed?">
        <textarea className="field-textarea" style={{ minHeight: 100 }}
          placeholder="Briefly describe the project, its background, and what problem it solves..."
          value={form.projectDescription} onChange={e => set('projectDescription', e.target.value)} />
      </Field>
      <Field label="Objectives" required hint="What specific goals will this project achieve?" helpTopic="objectives" onOpenHelp={setHelpTopic}>
        <textarea className="field-textarea"
          placeholder="List the key goals and objectives of this project..."
          value={form.objectives} onChange={e => set('objectives', e.target.value)} />
        {form.objectives.trim() && form.objectives.trim().split(/\s+/).length < 15 && (
          <div style={{ fontSize: 11, color: 'var(--warning-text, #92400e)', marginTop: 4 }}>Could you add a bit more detail? A specific, measurable goal helps IT scope this correctly.</div>
        )}
      </Field>
      <Field label="Deliverables" required hint="What will be produced or completed at the end?" helpTopic="deliverables" onOpenHelp={setHelpTopic}>
        <textarea className="field-textarea"
          placeholder="Describe the tangible outputs and deliverables..."
          value={form.deliverables} onChange={e => set('deliverables', e.target.value)} />
        {form.deliverables.trim() && form.deliverables.trim().split(/\s+/).length < 15 && (
          <div style={{ fontSize: 11, color: 'var(--warning-text, #92400e)', marginTop: 4 }}>Could you add a bit more detail? What tangible thing will exist when this is done?</div>
        )}
      </Field>
      <Field label="Organizational Benefits" required hint="How will this benefit The Woodlands Township?" helpTopic="organizationalBenefits" onOpenHelp={setHelpTopic}>
        <textarea className="field-textarea"
          placeholder="Explain the value and benefits to the Township and its residents..."
          value={form.organizationalBenefits} onChange={e => set('organizationalBenefits', e.target.value)} />
      </Field>
    </div>
  );

  // ── Step 4 — Requirements & Attachments ───────────────────────
  const step3 = (
    <div>
      {/* Requirements */}
      <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 12 }}>Requirements</div>
      <div style={{ background: 'var(--info-bg, #eff6ff)', border: '1px solid var(--info, #93c5fd)', borderRadius: 10, padding: 16, marginBottom: 20 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>📋 Why add requirements?</div>
        <div style={{ fontSize: 13, color: 'var(--text1)', lineHeight: 1.6 }}>
          Requirements track what this project must deliver and questions for vendors. <strong>This step is optional</strong> but highly recommended for projects involving vendors or new software.
        </div>
      </div>

      {requirements.length > 0 && (
        <div className="card" style={{ overflow: 'hidden', marginBottom: 16 }}>
          <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 600, color: 'var(--text2)' }}>
            {requirements.length} requirement{requirements.length !== 1 ? 's' : ''} added
          </div>
          {requirements.map((req, idx) => (
            <div key={req.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 16px', borderBottom: idx < requirements.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', marginTop: 6, flexShrink: 0, background: req.isRequired ? 'var(--danger, #ef4444)' : 'var(--warning, #f59e0b)' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{req.question}</div>
                <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>
                  {req.category && <span>📁 {req.category} · </span>}
                  <span>{req.isRequired ? '🔴 Required' : '🟡 Nice-to-have'}</span>
                </div>
              </div>
              <button onClick={() => removeRequirement(req.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text2)', fontSize: 16, padding: '0 4px' }}>✕</button>
            </div>
          ))}
        </div>
      )}

      {showReqForm ? (
        <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 14 }}>New Requirement</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 4, display: 'block' }}>Category</label>
              <select className="field-select" style={{ width: '100%' }} value={reqForm.category} onChange={e => setReqForm(f => ({ ...f, category: e.target.value }))}>
                <option value="">— Select category —</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 4, display: 'block' }}>Priority</label>
              <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
                {[true, false].map(val => (
                  <label key={String(val)} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                    <input type="radio" checked={reqForm.isRequired === val} onChange={() => setReqForm(f => ({ ...f, isRequired: val }))} />
                    {val ? '🔴 Required' : '🟡 Nice-to-have'}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 4, display: 'block' }}>Requirement or Question</label>
            <textarea className="field-input" style={{ width: '100%', minHeight: 70, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
              placeholder='e.g. "Must integrate with Tyler EPL for permit data."'
              value={reqForm.question} onChange={e => setReqForm(f => ({ ...f, question: e.target.value }))} />
          </div>
          {reqError && <div style={{ color: 'var(--danger-text, #991b1b)', fontSize: 12, marginBottom: 8 }}>{reqError}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary btn-sm" onClick={addRequirement}>Add</button>
            <button className="btn btn-sm" onClick={() => { setShowReqForm(false); setReqError(''); }}>Cancel</button>
          </div>
        </div>
      ) : (
        <button className="btn" onClick={() => setShowReqForm(true)} style={{ marginBottom: 24 }}>+ Add Requirement</button>
      )}

      {requirements.length === 0 && !showReqForm && (
        <div style={{ fontSize: 13, color: 'var(--text2)', padding: '8px 0 24px' }}>
          No requirements added yet. You can add them now or IT can add them after submission.
        </div>
      )}

      {/* Attachments — only shown when admin enables the field */}
      {fieldEnabled('attachments') && (
        <>
          <div style={{ borderTop: '1px solid var(--border)', marginBottom: 20 }} />
          <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 6 }}>Attachments</div>
          <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
            Attach relevant documents, diagrams, or vendor materials. You can also add attachments later from the project detail page.
          </div>

          {!draftProjectId ? (
            <div style={{ padding: '20px', borderRadius: 10, border: '2px dashed var(--border2)', background: 'var(--surface2)', textAlign: 'center' }}>
              <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 10 }}>
                📎 Save a draft first to enable file uploads.
              </div>
              <button className="btn btn-sm" onClick={handleSaveDraft} disabled={savingDraft || !form.title.trim()}>
                {savingDraft ? 'Saving...' : '💾 Save Draft to Enable Uploads'}
              </button>
            </div>
          ) : (
            <div>
              {attachments.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  {attachments.map((a: any) => (
                    <div key={a.AttachmentID} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, background: 'var(--surface2)', border: '1px solid var(--border)', marginBottom: 8 }}>
                      <span style={{ fontSize: 18 }}>📎</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.FileName}</div>
                        <div style={{ fontSize: 11, color: 'var(--text3)' }}>{a.UploadedByName}</div>
                      </div>
                      <a href={a.BlobUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm">View</a>
                      <button className="btn btn-sm" style={{ color: 'var(--danger-text, #991b1b)' }} onClick={() => handleDeleteAttachment(a.AttachmentID)}>Remove</button>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ padding: '20px', borderRadius: 10, border: '2px dashed var(--border2)', background: 'var(--surface2)', textAlign: 'center' }}>
                <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 4 }}>📎 Drag and drop files here, or click to browse</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 12 }}>PDF, Word, Excel, images · Max 25MB each</div>
                <label style={{ cursor: uploadingFile ? 'default' : 'pointer' }}>
                  <input type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg" style={{ display: 'none' }}
                    disabled={uploadingFile}
                    onChange={async e => {
                    const files = Array.from(e.target.files || []);
                    if (!files.length || !draftProjectId) return;
                    setUploadingFile(true);
                    setSubmitError(null);
                    const errors: string[] = [];
                    try {
                      for (const file of files) {
                        if (file.size > 25 * 1024 * 1024) {
                          errors.push(`${file.name} exceeds the 25MB limit and was skipped.`);
                          continue;
                        }
                        try {
                          const { blobUrl, blobName } = await api.attachments.getSasToken(draftProjectId, file.name);
                          const uploadRes = await fetch(blobUrl, {
                            method: 'PUT',
                            headers: {
                              'x-ms-blob-type': 'BlockBlob',
                              'Content-Type': file.type || 'application/octet-stream',
                            },
                            body: file,
                          });
                          if (!uploadRes.ok) throw new Error(`Upload failed: ${uploadRes.status}`);
                          const permanentUrl = blobUrl.split('?')[0];
                          await api.attachments.create(draftProjectId, {
                            fileName:         file.name,
                            blobUrl:          permanentUrl,
                            blobName,
                            fileSizeBytes:    file.size,
                            mimeType:         file.type || null,
                            uploadedByUserID: dbUserID,
                            actorEmail:       user?.email || '',
                            actorName:        user?.name  || '',
                          });
                        } catch (fileErr: any) {
                          errors.push(`${file.name}: ${fileErr.message}`);
                        }
                      }
                      if (errors.length > 0) setSubmitError(errors.join('\n'));
                      loadAttachments();
                    } finally {
                      setUploadingFile(false);
                      e.target.value = '';
                    }
                  }}
                  />
                  <span className="btn btn-sm" style={{ pointerEvents: 'none' }}>
                    {uploadingFile ? 'Uploading...' : 'Choose Files'}
                  </span>
                </label>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  // ── Step 5 — Review & Submit ──────────────────────────────────
  const step4 = (
    <div>
      {isRevise && (
        <div style={{ padding: '12px 16px', background: 'var(--warning-bg, #fffbeb)', border: '1px solid var(--warning, #f59e0b)', borderRadius: 8, marginBottom: 16, fontSize: 13, color: 'var(--warning-text, #92400e)' }}>
          ✏️ You are resubmitting a revised version of this project for approval.
        </div>
      )}
      <div style={{ padding: '12px 16px', background: 'var(--info-bg, #eff6ff)', border: '1px solid var(--accent)', borderRadius: 8, marginBottom: 20, fontSize: 13, color: 'var(--accent)' }}>
        Once submitted, this will be sent to <strong>{selectedDept?.DirectorName || 'your department director'}</strong> for approval.
      </div>

      {submitError && (
        <div style={{ padding: 12, background: 'var(--danger-bg)', color: 'var(--danger-text)', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
          {submitError}
        </div>
      )}

      {submissionBlocked && (
        <div style={{ padding: 12, background: 'var(--warning-bg, #fffbeb)', color: 'var(--warning-text, #92400e)', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
          🚧 Maintenance mode is active — new submissions are temporarily disabled. You can still save this as a draft and submit once maintenance mode ends.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div className="card" style={{ padding: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>Project Info</div>
          {[
            ['Project Name',    form.title],
            ['Department',      selectedDept?.DepartmentName || '—'],
            ['Business Owner',  form.businessOwnerName],
            ['Business Owner Email', form.businessOwnerEmail],
            ['Project Manager', form.projectManagerName],
            ['Project Manager Email', form.projectManagerEmail],
          ].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', gap: 12, padding: '7px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
              <span style={{ color: 'var(--text2)', width: 130, flexShrink: 0 }}>{k}</span>
              <span style={{ fontWeight: 500 }}>{v || '—'}</span>
            </div>
          ))}
        </div>

        <div className="card" style={{ padding: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>Schedule & Budget</div>
          {[
            ...(fieldEnabled('priority') ? [['Priority', form.priority || '—']] : []),
            ['Budgeted',   form.budgeted || 'TBD'],
            ['Amount',     form.budgeted === 'Yes' && form.amountBudgeted ? `$${parseFloat(form.amountBudgeted).toLocaleString()}` : '—'],
            ['Start Date', form.startDate || '—'],
            ['End Date',   form.endDate   || '—'],
          ].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', gap: 12, padding: '7px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
              <span style={{ color: 'var(--text2)', width: 130, flexShrink: 0 }}>{k}</span>
              <span style={{ fontWeight: 500 }}>{v || '—'}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>Description</div>
        <div style={{ fontSize: 13, color: 'var(--text1)', lineHeight: 1.6, marginBottom: 12 }}>{form.projectDescription}</div>
        {form.objectives && (
          <>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Objectives</div>
            <div style={{ fontSize: 13, color: 'var(--text1)', lineHeight: 1.6, marginBottom: 12 }}>{form.objectives}</div>
          </>
        )}
        {form.deliverables && (
          <>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Deliverables</div>
            <div style={{ fontSize: 13, color: 'var(--text1)', lineHeight: 1.6, marginBottom: 12 }}>{form.deliverables}</div>
          </>
        )}
        {form.organizationalBenefits && (
          <>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Organizational Benefits</div>
            <div style={{ fontSize: 13, color: 'var(--text1)', lineHeight: 1.6 }}>{form.organizationalBenefits}</div>
          </>
        )}
      </div>

      {requirements.length > 0 && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>Requirements ({requirements.length})</div>
          {requirements.map((req, idx) => (
            <div key={req.id} style={{ display: 'flex', gap: 10, padding: '7px 0', borderBottom: idx < requirements.length - 1 ? '1px solid var(--border)' : 'none', fontSize: 13 }}>
              <span>{req.isRequired ? '🔴' : '🟡'}</span>
              <div>
                <div style={{ fontWeight: 500 }}>{req.question}</div>
                {req.category && <div style={{ fontSize: 11, color: 'var(--text2)' }}>{req.category}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {attachments.length > 0 && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>Attachments ({attachments.length})</div>
          {attachments.map((a: any) => (
            <div key={a.AttachmentID} style={{ fontSize: 13, padding: '6px 0', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>📎</span><span style={{ fontWeight: 500 }}>{a.FileName}</span>
            </div>
          ))}
        </div>
      )}

      <div className="card" style={{ padding: 16 }}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>Approval Chain</div>
        {chainSteps.length === 0 ? (
          // Fallback if chain hasn't loaded yet or fetch failed
          [
            { num: 1, name: selectedDept?.DirectorName || 'Department Director', role: 'Director'  },
            { num: 2, name: selectedDept?.AGMName      || 'EMT Contact',         role: 'EMT'       },
            { num: 3, name: 'IT / CTIO',                                          role: 'IT Review' },
          ].map(s => (
            <div key={s.num} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: s.num < 3 ? '1px solid var(--border)' : 'none' }}>
              <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--border2)', color: 'var(--text2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, flexShrink: 0 }}>{s.num}</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{s.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>{s.role}</div>
              </div>
            </div>
          ))
        ) : (
          chainSteps.map((s, i) => {
            // Resolve display name based on step type
            const stepName = s.StepName === 'AGM' ? 'EMT' : s.StepName;
            let personName = stepName;
            if (s.StepName === 'Director') personName = selectedDept?.DirectorName || 'Department Director';
            else if (s.StepName === 'EMT' || s.StepName === 'AGM') personName = selectedDept?.AGMName || 'EMT Contact';
            else if (s.StepName === 'IT')  personName = 'IT / CTIO';
            else if (s.StepName === 'CEO') personName = 'CEO';

            // Auto-skip indicator: Director/higher roles skip their own tier
            const willSkip =
              (s.StepName === 'Director' && (isDirector || isAdmin)) ||
              ((s.StepName === 'EMT' || s.StepName === 'AGM') && isAdmin);

            return (
              <div key={s.StepName} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: i < chainSteps.length - 1 ? '1px solid var(--border)' : 'none', opacity: willSkip ? 0.5 : 1 }}>
                <div style={{ width: 24, height: 24, borderRadius: '50%', background: willSkip ? 'var(--border)' : 'var(--border2)', color: 'var(--text2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, flexShrink: 0 }}>
                  {willSkip ? '—' : i + 1}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: willSkip ? 'var(--text3)' : 'var(--text1)' }}>
                    {personName}
                    {willSkip && <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--text3)', fontWeight: 400 }}>(auto-skipped)</span>}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)' }}>{stepName}</div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  const stepContent = [step0, step1, step2, step3, step4][step];
  const stepLabels  = ['Details & Dates', 'Description', 'Requirements & Attachments', 'Review & Submit'];

  if (loadingProject) {
    return <div style={{ padding: 48, textAlign: 'center', color: 'var(--text2)' }}>Loading project...</div>;
  }

  return (
    <div>
      {helpTopic && <HelpModal topic={helpTopic} onClose={() => setHelpTopic(null)} />}

      <div className="page-header">
        <div>
          <h1 className="page-title">
            {isRevise ? 'Revise & Resubmit' : draftId ? 'Resume Draft' : 'New IT Project Request'}
          </h1>
          <p className="page-sub">
            {isRevise ? 'Update your project and resubmit for approval' : 'Fill out all required fields to submit for director approval'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {draftSavedAt && (
            <span style={{ fontSize: 12, color: 'var(--success-text, #065f46)' }}>
              ✓ Draft saved {draftSavedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
            </span>
          )}
          <button className="btn" onClick={() => navigate('/')}>← Back</button>
        </div>
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        <StepBar current={step} />

        {validationErrors.length > 0 && (
          <div style={{ margin: '16px 24px 0', padding: 12, background: 'var(--danger-bg)', borderRadius: 8, border: '1px solid var(--danger)' }}>
            {validationErrors.map((e, i) => (
              <div key={i} style={{ fontSize: 13, color: 'var(--danger-text)', marginBottom: i < validationErrors.length - 1 ? 4 : 0 }}>· {e}</div>
            ))}
          </div>
        )}

        {submitError && step < 4 && (
          <div style={{ margin: '16px 24px 0', padding: 12, background: 'var(--danger-bg)', borderRadius: 8, border: '1px solid var(--danger)', fontSize: 13, color: 'var(--danger-text)' }}>
            {submitError}
          </div>
        )}

        <div style={{ padding: 24 }}>{stepContent}</div>

        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '16px 24px', borderTop: '1px solid var(--border)', background: 'var(--surface2)',
        }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: 'var(--text2)' }}>
              Step {step + 1} of 5 · Fields marked <span style={{ color: 'var(--danger)' }}>*</span> are required
            </span>
            {/* Save draft on steps 1–4, not Review, not in revise mode */}
            {!isRevise && step < 4 && (
              <button className="btn btn-sm" onClick={handleSaveDraft} disabled={savingDraft || !form.title.trim()} style={{ marginLeft: 8 }}>
                {savingDraft ? 'Saving...' : '💾 Save Draft'}
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {step > 0 && <button className="btn" onClick={goBack}>← Previous</button>}
            {step < 4 ? (
              <button className="btn btn-primary" onClick={goNext}>Next: {stepLabels[step]} →</button>
            ) : (
              <button className="btn btn-primary btn-lg" onClick={handleSubmit} disabled={submitting || submissionBlocked} title={submissionBlocked ? 'Maintenance mode is active' : undefined}>
                {submitting ? 'Submitting...' : isRevise ? '✓ Resubmit for Approval' : '✓ Submit for Approval'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
